import React, { useEffect, useState } from "react"
import {
  Container, Card, CardBody, Modal, Form, Label, Input,
} from "reactstrap"
import Breadcrumbs from "components/Common/Breadcrumb"
import Select from "react-select"
import Swal from "sweetalert2"
import axios from "axios"
import configure from "configure"
import Loader from "pages/Separate/Loader"
import secureLocalStorage from "react-secure-storage"
import moment from "moment"
import { fetchNodeData, FetchNodeDeleteData } from "utils/common-fetchData"

const EMPTY_FORM = {
  title: "",
  roles: [],
  departments: [],
  file: null,
}

const ZithasSOP = () => {
  const [loading, setLoading] = useState(false)
  const [sopList, setSopList] = useState([])
  const [modal, setModal] = useState(false)
  const [formData, setFormData] = useState(EMPTY_FORM)
  const [Roles, setRoles] = useState([])
  const [Departments, setDepartments] = useState([])

  useEffect(() => {
    document.title = "Zithas SOP | Zithas Technologies"
    fetchAll()
  }, [])

  const fetchAll = async () => {
    await Promise.all([fetchSOPs(), fetchRoles(), fetchDepartments()])
  }

  const fetchSOPs = async () => {
    const token = secureLocalStorage.getItem("auth_token")
    const res = await fetchNodeData(setLoading, "sop/getAll", token)
    if (res?.success) setSopList(res.data || [])
  }

  const fetchRoles = async () => {
    try {
      const res = await axios.get(`${configure.apiUrl}/staffData`, {
        headers: { Accept: "application/json" },
      })
      setRoles((res.data?.data?.roles || []).map(r => ({ value: r.id, label: r.role_name })))
    } catch (e) {
      console.log("Roles fetch error", e)
    }
  }

  const fetchDepartments = async () => {
    try {
      const res = await axios.get(`${configure.apiUrl}/departments`, {
        headers: { Accept: "application/json" },
      })
      setDepartments((res.data?.data?.department || []).map(d => ({ value: d.id, label: d.department_name })))
    } catch (e) {
      console.log("Departments fetch error", e)
    }
  }

  const openAddModal = () => {
    setFormData(EMPTY_FORM)
    setModal(true)
  }

  const handleSubmit = async e => {
    e.preventDefault()
    if (!formData.file) {
      Swal.fire({ icon: "warning", title: "Please select a document to upload." })
      return
    }

    const fd = new FormData()
    fd.append("title", formData.title)
    fd.append("roles", formData.roles.map(r => r.value).join(",") || "")
    fd.append("departments", formData.departments.map(d => d.value).join(",") || "")
    fd.append("file", formData.file)
    fd.append("user_id", localStorage.getItem("auth_id"))

    setLoading(true)
    try {
      const token = secureLocalStorage.getItem("auth_token")
      const res = await axios.post(
        `${configure.nodeApi}/sop/add`,
        fd,
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } }
      )
      if (res.data?.success) {
        Swal.fire({ icon: "success", title: "SOP uploaded successfully", timer: 1500, showConfirmButton: false })
        setModal(false)
        fetchSOPs()
      } else {
        Swal.fire({ icon: "error", title: "Upload failed", text: res.data?.message })
      }
    } catch (err) {
      Swal.fire({ icon: "error", title: "Upload failed", text: err.message })
    }
    setLoading(false)
  }

  const handleDelete = async id => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This SOP will be permanently deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    })
    if (result.isConfirmed) {
      const res = await FetchNodeDeleteData(setLoading, "sop/delete", id)
      if (res?.status === 200) {
        Swal.fire({ icon: "success", title: "Deleted!", timer: 1200, showConfirmButton: false })
        fetchSOPs()
      } else {
        Swal.fire({ icon: "error", title: "Something went wrong." })
      }
    }
  }

  const getRoleLabels = roles => {
    if (!roles) return <span className="text-muted" style={{ fontSize: 12 }}>All Roles</span>
    const ids = String(roles).split(",").map(s => s.trim())
    const found = Roles.filter(r => ids.includes(String(r.value)))
    if (!found.length) return <span className="text-muted" style={{ fontSize: 12 }}>All Roles</span>
    return (
      <div className="d-flex flex-wrap gap-1">
        {found.map(r => (
          <span key={r.value} className="badge bg-white text-dark border" style={{ fontSize: 11 }}>{r.label}</span>
        ))}
      </div>
    )
  }

  const getDeptLabels = depts => {
    if (!depts) return <span className="text-muted" style={{ fontSize: 12 }}>All Departments</span>
    const ids = String(depts).split(",").map(s => s.trim())
    const found = Departments.filter(d => ids.includes(String(d.value)))
    if (!found.length) return <span className="text-muted" style={{ fontSize: 12 }}>All Departments</span>
    return (
      <div className="d-flex flex-wrap gap-1">
        {found.map(d => (
          <span key={d.value} className="badge bg-white text-dark border" style={{ fontSize: 11 }}>{d.label}</span>
        ))}
      </div>
    )
  }

  return (
    <React.Fragment>
      {loading && <Loader />}
      <div className="page-content mb-4">
        <Container fluid>
          <Breadcrumbs title="Dashboard" breadcrumbItem="Zithas SOP" />
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="d-flex align-items-center gap-2">
                  <div
                    style={{
                      width: 34, height: 34, borderRadius: 8,
                      background: "linear-gradient(135deg, #752a97, #a855c9)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                  >
                    <i className="fas fa-file-alt text-white" style={{ fontSize: 15 }}></i>
                  </div>
                  <span className="fw-bold text-purple" style={{ fontSize: 15 }}>Zithas SOP</span>
                </div>
                <button className="border-radius btn btn-purple btn-sm" onClick={openAddModal}>
                  <i className="fas fa-plus me-2"></i>Add SOP
                </button>
              </div>

              {sopList.length === 0 && !loading ? (
                <div className="text-center text-muted py-5">
                  <i className="fas fa-folder-open fa-3x mb-3 d-block"></i>
                  <p>No SOPs uploaded yet. Click <strong>Add SOP</strong> to get started.</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-bordered mb-0" style={{ fontSize: 13 }}>
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: 40 }}>#</th>
                        <th>Title</th>
                        <th>Roles</th>
                        <th>Departments</th>
                        <th style={{ width: 140 }}>Uploaded On</th>
                        <th className="text-center" style={{ width: 120 }}>Document</th>
                        <th className="text-center" style={{ width: 80 }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sopList.map((sop, i) => (
                        <tr key={sop.id} className="row-hover">
                          <td>{i + 1}</td>
                          <td className="fw-semibold">{sop.title}</td>
                          <td>{getRoleLabels(sop.roles)}</td>
                          <td>{getDeptLabels(sop.departments)}</td>
                          <td>{moment(sop.created_at).format("DD MMM YYYY")}</td>
                          <td className="text-center">
                            <a
                              href={sop.file_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-outline-primary"
                              title="View / Download"
                            >
                              <i className="fas fa-file-download me-1"></i>View
                            </a>
                          </td>
                          <td className="text-center">
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleDelete(sop.id)}
                              title="Delete"
                            >
                              <i className="fas fa-trash-alt"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardBody>
          </Card>
        </Container>
      </div>

      {/* Add SOP Modal */}
      <Modal isOpen={modal} size="lg" centered toggle={() => setModal(false)}>
        <div className="modal-header px-4 py-3">
          <h5 className="modal-title mt-0" style={{ fontSize: 15 }}>
            <i className="fas fa-file-upload me-2 text-purple"></i>Upload SOP
          </h5>
          <button type="button" onClick={() => setModal(false)} className="btn-modal-close">
            <span className="btn-modal-span">&times;</span>
          </button>
        </div>

        <div className="modal-body px-4 py-3">
          <Form onSubmit={handleSubmit}>
            {/* Title */}
            <div className="mb-3">
              <Label className="mb-1" style={{ fontSize: 12 }}>
                SOP Title <span className="text-danger">*</span>
              </Label>
              <Input
                type="text"
                placeholder="Enter SOP title"
                value={formData.title}
                onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
                required
              />
            </div>

            {/* Document upload */}
            <div className="mb-3">
              <Label className="mb-1" style={{ fontSize: 12 }}>
                Document <span className="text-danger">*</span>
                <span className="text-muted ms-1" style={{ fontWeight: 400, fontSize: 11 }}>
                  (PDF, DOC, DOCX, PPT, PPTX)
                </span>
              </Label>
              <Input
                type="file"
                accept=".pdf,.doc,.docx,.ppt,.pptx"
                onChange={e => setFormData(prev => ({ ...prev, file: e.target.files[0] || null }))}
              />
            </div>

            <div className="row g-3">
              {/* Roles */}
              <div className="col-md-6">
                <Label className="mb-1" style={{ fontSize: 12 }}>
                  Roles{" "}
                  <span className="text-muted" style={{ fontWeight: 400, fontSize: 11 }}>
                    (optional — leave empty for all)
                  </span>
                </Label>
                <Select
                  placeholder="Select roles…"
                  className="basic-multi-select"
                  classNamePrefix="select"
                  options={Roles}
                  value={formData.roles}
                  onChange={val => setFormData(prev => ({ ...prev, roles: val || [] }))}
                  isMulti
                />
              </div>

              {/* Departments */}
              <div className="col-md-6">
                <Label className="mb-1" style={{ fontSize: 12 }}>
                  Departments{" "}
                  <span className="text-muted" style={{ fontWeight: 400, fontSize: 11 }}>
                    (optional — leave empty for all)
                  </span>
                </Label>
                <Select
                  placeholder="Select departments…"
                  className="basic-multi-select"
                  classNamePrefix="select"
                  options={Departments}
                  value={formData.departments}
                  onChange={val => setFormData(prev => ({ ...prev, departments: val || [] }))}
                  isMulti
                />
              </div>
            </div>

            <div className="mt-4 pt-3" style={{ borderTop: "1px solid #e9ecef" }}>
              <button type="submit" className="border-radius btn btn-purple btn-sm px-4">
                <i className="fas fa-upload me-2"></i>Upload SOP
              </button>
              <button
                type="button"
                className="border-radius btn btn-light btn-sm px-4 ms-2"
                onClick={() => setModal(false)}
              >
                Cancel
              </button>
            </div>
          </Form>
        </div>
      </Modal>
    </React.Fragment>
  )
}

export default ZithasSOP
